// Unpublished Work © 2023-2024 Deere & Company.
import axios from "../api";
import {
  ConsolidatedHURequest,
  HandlingUnitDetailsResponse,
  HandlingUnitHeaderItem,
  HandlingUnitListResponse,
  HuTrackingResponse,
  IHandlingUnitApi,
} from "./handlingUnitInterfaces";

const HandlingUnitApi: IHandlingUnitApi = {
  getAllHandlingUnits: async (
    handlingUnitServiceUrl: string,
    accountId: string,
    token: string,
    lang?: string,
    countryCode?: string,
    withLeadingZeros?: boolean
  ): Promise<HandlingUnitHeaderItem[]> => {
    const response = await axios.getWithRetries(
      `${handlingUnitServiceUrl}/v1/handlingunit`,
      {
        headers: {
          "dealer-id": accountId,
          Authorization: `Bearer ${token}`,
        },
        params: { country: countryCode, lang, withLeadingZeros },
      }
    );

    return response.data.handlingUnits.map(
      (handlingUnit: any, index: number) =>
        ({
          id: `huItem-${index}`,
          HUNumber: handlingUnit.huNumber,
          serviceLevel: handlingUnit.serviceLevel,
          documentId: handlingUnit.nfeNumber,
          carrierName: handlingUnit.carrierName,
          parcelDescription: handlingUnit.parcelDescription,
          totalWeight: handlingUnit.totalWeight,
          shipDate: handlingUnit.shipDate,
          trackingNo: handlingUnit.trackNo,
          remitoNo: handlingUnit.remitoNo,
          trackingUrl: handlingUnit.url,
          partNumber: handlingUnit.partNumbers,
          orderNumber: handlingUnit.orderNumbers,
          trackingStatuses: handlingUnit.trackingStatuses,
          dealerId: handlingUnit.dealerId,
        } as HandlingUnitHeaderItem)
    );
  },

  getAllHandlingUnitsV2: async (
    handlingUnitServiceUrl: string,
    accountId: string,
    token: string,
    lang: string,
    countryCode: string,
    filters: { pageNumber: number; pageSize: number }
  ): Promise<HandlingUnitListResponse> => {
    const response = await axios.getWithRetries(
      `${handlingUnitServiceUrl}/v2/handlingunit`,
      {
        headers: {
          "dealer-id": accountId,
          Authorization: `Bearer ${token}`,
        },
        params: { country: countryCode, lang, ...filters },
      }
    );
    return response.data;
  },

  getAllHandlingUnitsAsExcelSheet(
    handlingUnitServiceUrl: string,
    accountId: string,
    columnHeaders: string,
    token: string,
    lang?: string,
    countryCode?: string,
    displayTracking?: boolean,
    withLeadingZeros?: boolean
  ): Promise<any> {
    return axios.getWithRetries(
      `${handlingUnitServiceUrl}/v1/handlingunit/excel/download`,
      {
        responseType: "blob",
        headers: {
          "dealer-id": accountId,
          Authorization: `Bearer ${token}`,
          Accept:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
        params: {
          country: countryCode,
          "column-headers": columnHeaders,
          lang,
          displayTracking: displayTracking || false,
          withLeadingZeros,
        },
      }
    );
  },

  getHuDetails: async (
    HANDLING_UNIT_SERVICE_URL: string,
    huNumber: string,
    accountId: string,
    token: string,
    lang?: string,
    shipDate?: string,
    countryCode?: string,
    withLeadingZeros?: boolean
  ): Promise<HandlingUnitDetailsResponse> => {
    const response = await axios.getWithRetries(
      `${HANDLING_UNIT_SERVICE_URL}/v1/handlingunit/${huNumber}`,
      {
        headers: {
          "dealer-id": accountId,
          "ship-date": shipDate || "",
          Authorization: `Bearer ${token}`,
        },
        params: { country: countryCode, lang, withLeadingZeros },
      }
    );

    const huDetailsResponse = response.data.handlingUnitDetails;

    return huDetailsResponse;
  },

  getHuDetailsExcelSheet: async (
    HANDLING_UNIT_SERVICE_URL: string,
    huNumber: string,
    accountId: string,
    columnHeaders: string,
    token: string,
    lang?: string,
    shipDate?: string,
    countryCode?: string
  ): Promise<any> =>
    axios.getWithRetries(
      `${HANDLING_UNIT_SERVICE_URL}/v1/handlingunit/${huNumber}/excel/download`,
      {
        responseType: "blob",
        headers: {
          "dealer-id": accountId,
          "ship-date": shipDate || "",
          Authorization: `Bearer ${token}`,
          Accept:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
        params: {
          country: countryCode,
          "column-headers": columnHeaders,
          ...(lang && { lang }),
        },
      }
    ),

  getAllHandlingUnitsAsExcelSheetV2(
    handlingUnitServiceUrl: string,
    accountId: string,
    token: string,
    countryCode: string,
    filters: { pageNumber: number; pageSize: number },
    columnHeaders: string,
    lang?: string
  ): Promise<any> {
    return axios.getWithRetries(
      `${handlingUnitServiceUrl}/v2/handlingunit/excel/download`,
      {
        responseType: "blob",
        headers: {
          "dealer-id": accountId,
          Authorization: `Bearer ${token}`,
          Accept:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
        params: {
          country: countryCode,
          lang,
          ...filters,
          "column-headers": columnHeaders,
        },
      }
    );
  },

  getWalleniusTrackingUrl: async (
    HANDLING_UNIT_SERVICE_URL: string,
    huNumber: string,
    businessPartnerShipTo: string,
    token: string
  ): Promise<string> =>
    (
      await axios.getWithRetries(
        `${HANDLING_UNIT_SERVICE_URL}/v1/handlingunit/${huNumber}/wallenius`,
        {
          headers: {
            "business-partner-ship-to": businessPartnerShipTo,
            Authorization: `Bearer ${token}`,
          },
        }
      )
    ).data.trackingLink,

  getWalleniusTrackingUrlV2: async (
    HANDLING_UNIT_SERVICE_URL: string,
    huNumber: string,
    dealerId: string,
    token: string
  ): Promise<string> =>
    (
      await axios.getWithRetries(
        `${HANDLING_UNIT_SERVICE_URL}/v2/handlingunit/${huNumber}/wallenius`,
        {
          headers: {
            "dealer-id": dealerId,
            Authorization: `Bearer ${token}`,
          },
        }
      )
    ).data.trackingLink,

  getUFCToken: async (
    HANDLING_UNIT_SERVICE_URL: string,
    token: string
  ): Promise<string> =>
    // eslint-disable-next-line no-return-await
    (
      await axios.getWithRetries(
        `${HANDLING_UNIT_SERVICE_URL}/v1/ufc/getToken`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
    ).data,

  getHuTrackingStatus: async (
    HANDLING_UNIT_SERVICE_URL: string,
    huNumber: string,
    accountId: string,
    token: string,
    lang?: string
  ): Promise<HuTrackingResponse> =>
    // eslint-disable-next-line no-return-await
    (
      await axios.getWithRetries(
        `${HANDLING_UNIT_SERVICE_URL}/v1/handlingunit/tracking/${huNumber}`,
        {
          headers: {
            "dealer-id": accountId,
            Authorization: `Bearer ${token}`,
          },
          params: { lang },
        }
      )
    ).data,

  getHuRemitoDocument: async (
    HANDLING_UNIT_SERVICE_URL: string,
    remitoNumber: string,
    token: string
  ): Promise<any> =>
    axios.getWithRetries(
      `${HANDLING_UNIT_SERVICE_URL}/v2/handlingunit/remito/${remitoNumber}/download`,
      {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/pdf",
        },
      }
    ),

  getConsolidatedByHuExcel: async (
    HANDLING_UNIT_SERVICE_URL: string,
    request: ConsolidatedHURequest,
    token: string
  ): Promise<any> =>
    axios.post(
      `${HANDLING_UNIT_SERVICE_URL}/v1/handlingunit/consolidate-by-hu/excel/download`,
      request,
      {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
      }
    ),
};

export default HandlingUnitApi;
